import { chakra } from '@chakra-ui/react'
import { useRef, useState } from 'react'
import ComponentProps from '../../types/ComponentProps'
import {
  videoCompleteEvent,
  videoProgressEvent,
  videoStartEvent,
  VideoTrackingProps,
} from './MediaVideoTracking'

export interface MediaVideoProps extends ComponentProps {
  videoSrc: string
  isAutoPlay?: boolean
  isMuted?: boolean
  showControls?: boolean
  loop?: boolean
}

/**
 * Represents a wrapper around a video which includes tracking.
 * @param videoSrc - Source of the video to be played
 * @param isAutoPlay - Indicates whether the video automatically plays when rendered
 * @param isMuted - Indicates whether the video will be muted when playing, depending on whether isAutoPlay is set.
 * @param showControls - Indicates whether we should show or hide the default controls on a video
 * @param loop - Indicate whether the video should loop or not.
 * @returns MediaVideo
 */
export default chakra(MediaVideo)

function MediaVideo({
  videoSrc,
  isAutoPlay,
  isMuted = isAutoPlay,
  showControls,
  loop,
  ...props
}: MediaVideoProps) {
  const videoRef = useRef<HTMLVideoElement>(null)
  const [videoStartedOnce, setVideoStartedOnce] = useState<boolean>(false)
  const defaultVideoProperties: VideoTrackingProps = {
    video_title: videoSrc,
    video_url: videoSrc,
    video_duration: videoRef.current ? videoRef.current.duration : 0,
    video_provider: 'backoffice',
  }

  function videoStart() {
    if (videoStartedOnce) {
      return
    }

    setVideoStartedOnce(true)
    videoStartEvent(videoRef, defaultVideoProperties)
  }

  return (
    <chakra.video
      ref={videoRef}
      muted={isMuted}
      autoPlay={isAutoPlay}
      controls={showControls}
      onPlay={() => videoStart()}
      onTimeUpdate={() => videoProgressEvent(videoRef, defaultVideoProperties)}
      onEnded={() => videoCompleteEvent(videoRef, defaultVideoProperties)}
      controlsList='nodownload'
      loop={loop}
      {...props}
    >
      <source src={videoSrc} type='video/mp4' />
      Your browser does not support the video tag.
    </chakra.video>
  )
}
